/* eslint-disable no-console */
import {
  AdvancedSelect,
  Button,
  Input,
  Modal,
  TipBox,
} from '@orobix/orobix-fe-components'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import { ProductClass } from '../../../types'
import { post } from '../../../utils/api'
import { validateDatasetName } from '../../../utils/datasetValidation'
import useFetchDatasets from '../../DatasetSelect/useFetchDatasets'
import { USER_LEVEL } from '../../Settings/definitions'
import { SettingsContext } from '../../Settings/SettingsContext'
import { MAX_FILE_SIZE, MAX_FILES } from '../definitions'
import { CandidateClassImage, UploadImageGroup } from '../types'
import useProductClass from '../useProductClass'
import useStyles from './ImageGroupsModal.styles'

// Logging prefix for easy filtering in console
const LOG_PREFIX = '[ImageGroupsModal]'

type SelectOption = {
  label: string
  value: string
}

interface ImageGroupsModalProps {
  candidateClassImages: CandidateClassImage[]
  productId: number
  withClass: boolean
  isLoading: boolean
  onConfirm: (imageGroups: UploadImageGroup[]) => void
  onClose: () => void
}

const ImageGroupsModal = ({
  candidateClassImages,
  productId,
  withClass,
  isLoading,
  onConfirm,
  onClose,
}: ImageGroupsModalProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { settings } = useContext(SettingsContext)
  const isExpertUser = settings.preference__user_level === USER_LEVEL.expert

  // Check if we're in the dataset management section (URL contains 'dataset')
  const isInDatasetSection = location.pathname.includes('/dataset')

  // Show dataset selection only for expert users and not in dataset section
  const showDatasetSelection = isExpertUser && !isInDatasetSection

  const NOT_ANNOTATED_OPTION: SelectOption = {
    label: `${t('non-annotated')}`,
    value: 'non-annotated',
  }

  const classes = useStyles()
  const { productClasses } = useProductClass(productId)
  const {
    datasets,
    isFetching: isFetchingDatasets,
    refetch: refetchDatasets,
  } = useFetchDatasets(productId)
  const [selectOptions, setSelectOptions] = useState<SelectOption[]>([])
  const [imageGroups, setImageGroups] = useState<UploadImageGroup[]>([])
  const [totalImages, setTotalImages] = useState<number>(0)
  const [totalImagesSize, setTotalImagesSize] = useState<number>(0)
  const [selectedDatasets, setSelectedDatasets] = useState<SelectOption[]>([])
  const [newDatasetName, setNewDatasetName] = useState<string>('')
  const [newDatasetError, setNewDatasetError] = useState<string>('')
  const [isCreatingDataset, setIsCreatingDataset] = useState<boolean>(false)

  useEffect(() => {
    const imagesCount = imageGroups.reduce(
      (count, imageGroup) => count + imageGroup.images.length,
      0
    )
    setTotalImages(imagesCount)
    const imagesSize = imageGroups.reduce(
      (count, imageGroup) =>
        count + imageGroup.images.reduce((size, image) => size + image.size, 0),
      0
    )
    setTotalImagesSize(imagesSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageGroups, candidateClassImages.length])

  useEffect(() => {
    if (productClasses && withClass) {
      setSelectOptions(
        productClasses.results
          .map((productClass: ProductClass) => ({
            label: productClass.productClass,
            value: productClass.productClass,
          }))
          .concat(NOT_ANNOTATED_OPTION)
      )
    } else {
      setSelectOptions([NOT_ANNOTATED_OPTION])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productClasses, withClass, candidateClassImages.length])

  // Re-validate dataset name when datasets are loaded
  useEffect(() => {
    if (newDatasetName && datasets) {
      const result = validateDatasetName(newDatasetName)
      if (!result.valid) {
        setNewDatasetError(t(result.errorKey! as any))
      } else {
        const datasetExists = datasets.some(
          (dataset) =>
            dataset.name.toLowerCase() === newDatasetName.toLowerCase()
        )
        if (datasetExists) {
          setNewDatasetError(t('A dataset with this name already exists'))
        } else {
          setNewDatasetError('')
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasets])

  useEffect(() => {
    const newImageGroups: UploadImageGroup[] = []

    // Map each candidate class image to a product class
    candidateClassImages.forEach((candidateClassImage) => {
      const matchedProductClass = productClasses?.results.find(
        (productClass: ProductClass) =>
          productClass.productClass === candidateClassImage.candidateClass
      )

      const existingImageGroup = newImageGroups.find(
        (imageGroup) =>
          imageGroup.canidateProductClass === candidateClassImage.candidateClass
      )

      if (existingImageGroup) {
        // Use existing image group if it exists
        existingImageGroup.images.push(candidateClassImage.image)
      } else {
        // Otherwise create a new image group
        const newImageGroup: UploadImageGroup = {
          images: [candidateClassImage.image],
          productId,
          productClass: matchedProductClass?.productClass,
          canidateProductClass: candidateClassImage.candidateClass,
          masks: undefined,
          mapping: undefined,
        }
        newImageGroups.push(newImageGroup)
      }
    })
    setImageGroups(newImageGroups)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productClasses, candidateClassImages.length])

  // Handle new dataset name change
  const handleNewDatasetNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target
    setNewDatasetName(value)
    const result = validateDatasetName(value)

    if (!result.valid) {
      setNewDatasetError(t(result.errorKey! as any))
    } else {
      // Check if dataset name already exists
      const datasetExists = datasets?.some(
        (dataset) => dataset.name.toLowerCase() === value.toLowerCase()
      )
      if (datasetExists) {
        setNewDatasetError(t('A dataset with this name already exists'))
      } else {
        setNewDatasetError('')
      }
    }
  }

  // Handle dataset selection change
  const handleDatasetSelectionChange = (value: readonly SelectOption[]) => {
    setSelectedDatasets([...value])
    if (value && value.length > 0) {
      // Clear new dataset name when existing dataset is selected
      setNewDatasetName('')
      setNewDatasetError('')
    }
  }

  // Handle confirm button click
  const handleConfirm = async () => {
    console.log(LOG_PREFIX, 'handleConfirm called', {
      selectedDatasetsCount: selectedDatasets?.length || 0,
      selectedDatasets: selectedDatasets?.map((d) => ({
        label: d.label,
        value: d.value,
      })),
      newDatasetName,
      imageGroupsCount: imageGroups.length,
      totalImages,
    })

    // Determine which datasets to use
    let datasetValues: string[] = []

    if (selectedDatasets && selectedDatasets.length > 0) {
      // Use existing datasets
      datasetValues = selectedDatasets.map((dataset) => dataset.value)
      console.log(LOG_PREFIX, 'Using existing datasets', { datasetValues })
    } else if (newDatasetName) {
      // Validate and use new dataset name
      const result = validateDatasetName(newDatasetName)

      if (!result.valid) {
        console.warn(LOG_PREFIX, 'Dataset name validation failed', {
          newDatasetName,
          errorKey: result.errorKey,
        })
        return // Don't proceed if validation fails
      }

      // Refetch datasets to get the latest list before checking for duplicates
      const freshDatasets = await refetchDatasets()

      // Check if dataset name already exists using fresh data
      const datasetExists = freshDatasets?.some(
        (dataset) => dataset.name.toLowerCase() === newDatasetName.toLowerCase()
      )
      if (datasetExists) {
        console.warn(LOG_PREFIX, 'Dataset name already exists', {
          newDatasetName,
        })
        setNewDatasetError(t('A dataset with this name already exists'))
        return // Don't proceed if dataset already exists
      }

      // Create empty dataset immediately to reserve the name
      setIsCreatingDataset(true)
      try {
        console.log(LOG_PREFIX, 'Creating empty dataset to reserve name', {
          newDatasetName,
          productId,
        })

        const response = await post<{ id: number; name: string }>('/dataset/', {
          name: newDatasetName,
          product_id: productId,
          observations_ids: [],
        })

        console.log(LOG_PREFIX, 'Empty dataset created successfully', {
          newDatasetName,
          datasetId: response.id,
        })

        // Use the actual dataset ID instead of 'new:' prefix
        datasetValues = [String(response.id)]
      } catch (error: unknown) {
        const axiosError = error as {
          message?: string
          response?: {
            status?: number
            data?: { name?: string[]; observations_ids?: string[] }
          }
        }
        console.error(LOG_PREFIX, 'Failed to create empty dataset', {
          newDatasetName,
          error: axiosError?.message || error,
          response: axiosError?.response?.data,
        })

        // Check if it's a duplicate name error (race condition)
        const isDuplicateNameError = axiosError?.response?.data?.name?.some(
          (msg) =>
            msg.toLowerCase().includes('already exists') ||
            msg.toLowerCase().includes('già esiste')
        )

        if (isDuplicateNameError) {
          setNewDatasetError(t('A dataset with this name already exists'))
        } else {
          setNewDatasetError(
            t(
              'An error occurred. Please try again later or contact the support.'
            )
          )
        }
        setIsCreatingDataset(false)
        return // Don't proceed if dataset creation fails
      }
      setIsCreatingDataset(false)
    } else {
      console.log(
        LOG_PREFIX,
        'No dataset selected, uploading images without dataset association'
      )
    }

    const updatedImageGroups = imageGroups.map((imageGroup) => ({
      ...imageGroup,
      datasets: datasetValues,
    }))

    console.log(LOG_PREFIX, 'Calling onConfirm with imageGroups', {
      imageGroupsCount: updatedImageGroups.length,
      datasetValues,
      imageGroups: updatedImageGroups.map((g) => ({
        productClass: g.productClass,
        imagesCount: g.images.length,
        datasets: g.datasets,
      })),
    })

    onConfirm(updatedImageGroups)
  }

  return (
    <Modal
      data-testid="modalId"
      size="sm"
      onCloseRequest={() => {
        onClose()
      }}
      title={t('Upload images')}
      footer={
        <>
          <Button
            onClick={() => {
              onClose()
            }}
            label={t('Cancel')}
            type="default"
            buttonType="button"
          />
          <Button
            data-testid="btnConfirmId"
            onClick={handleConfirm}
            label={t('Confirm')}
            type="action"
            buttonType="button"
            loading={isLoading || isCreatingDataset}
            disabled={
              isLoading ||
              isCreatingDataset ||
              totalImages > MAX_FILES ||
              totalImagesSize === MAX_FILE_SIZE ||
              !!newDatasetError
            }
          />
        </>
      }
    >
      <p className={classes.sectionTitle}>{t('Annotate uploaded images')}</p>
      <div className={classes.annotationSection}>
        {(withClass && (
          <p>
            {imageGroups.length > 1 &&
              totalImages > 1 &&
              t('Select a class for each group of images')}
            {imageGroups.length === 1 &&
              totalImages > 1 &&
              t('Select a class for the group of {{count}} images', {
                count: totalImages,
              })}
            {totalImages === 1 && t('Select a class for uploaded image')}
          </p>
        )) || <p>{t('No class available for this product type')}</p>}
        <ul className={classes.ul}>
          {imageGroups.map((imageGroup, index) => (
            <AdvancedSelect
              id="productClass"
              name="productClass"
              key={uuidv4()}
              label={
                imageGroup.canidateProductClass
                  ? `${imageGroup.canidateProductClass} (${imageGroup.images.length})`
                  : undefined
              }
              value={
                selectOptions.find(
                  (option) => option.value === imageGroup.productClass
                ) || NOT_ANNOTATED_OPTION
              }
              options={selectOptions}
              onChange={(value) => {
                const newImageGroups = [...imageGroups]
                newImageGroups[index].productClass =
                  value?.value === NOT_ANNOTATED_OPTION.value
                    ? undefined
                    : value?.value
                setImageGroups(newImageGroups)
              }}
              isDisabled={!withClass}
            />
          ))}
        </ul>
      </div>
      {showDatasetSelection && (
        <>
          <AdvancedSelect
            id="dataset"
            name="dataset"
            label={t('Insert images into dataset')}
            placeholder={t('Select a dataset...')}
            value={selectedDatasets}
            options={
              datasets?.map((dataset) => ({
                label: dataset.name,
                value: String(dataset.id),
              })) || []
            }
            onChange={handleDatasetSelectionChange}
            onMenuOpen={refetchDatasets}
            isClearable
            isLoading={isFetchingDatasets}
            isMulti
          />
          <Input
            id="newDatasetName"
            name="newDatasetName"
            label={t('Or create new dataset')}
            placeholder={t('Enter dataset name...')}
            type="text"
            value={newDatasetName}
            onChange={handleNewDatasetNameChange}
            disabled={selectedDatasets.length > 0}
            error={!!newDatasetError}
            errorMessage={newDatasetError}
          />
        </>
      )}
      {totalImages > MAX_FILES && (
        <TipBox>
          {t('You can upload a maximum of {{maxFiles}} images', {
            maxFiles: MAX_FILES,
          })}
        </TipBox>
      )}
      {totalImagesSize > MAX_FILE_SIZE && (
        <TipBox>
          {t('You can upload a maximum of {{maxFileSize}} MB', {
            maxFileSize: MAX_FILE_SIZE / (1024 * 1024),
          })}
        </TipBox>
      )}
    </Modal>
  )
}

export default ImageGroupsModal
